import { config } from '@iheartjane/config';
import { asyncFlagProvider } from '@iheartjane/feature-flags';
import '@iheartjane/reefer/styles/index.css';
import type { AppMode } from '@iheartjane/types';
import { lazy } from 'react';
import { createRoot } from 'react-dom/client';

import { ConfigureStore, bootstrap } from '@jane/monolith/headless';
import { ErrorBoundary, LegacyAppWrapper } from '@jane/shared-ecomm/components';
import { ResizeIframeProvider } from '@jane/shared-ecomm/providers';
import { EcommAppProvider, HeadlessAppProvider } from '@jane/shared/providers';

import { trackHeadlessError } from './trackHeadlessError';

const App = lazy(() => import('./app').then((a) => ({ default: a.App })));

// eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-module-scope
const container = document.getElementById('app');
if (!container) {
  throw new Error('Root element not found');
}
const root = createRoot(container);
// eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-module-scope
const appModeOverride = new URLSearchParams(window.location.search).get(
  'appmode'
) as AppMode;

(async () => {
  const FlagProvider = await asyncFlagProvider({
    application: { id: 'headless', version: config.appVersion },
    baseUrl: config.launchDarklyBaseUrl,
    clientSideID: config.launchDarklyClientID,
    eventsUrl: config.launchDarklyEventsUrl,
    streamUrl: config.launchDarklyStreamUrl,
  });
  const queryParams = new URLSearchParams(window.location.search);
  await bootstrap({ jdidOverride: queryParams.get('jdid') });

  root.render(
    <ErrorBoundary trackError={trackHeadlessError} withReeferThemeProvider>
      <HeadlessAppProvider>
        <EcommAppProvider appMode={appModeOverride ?? 'headless'}>
          <FlagProvider>
            <LegacyAppWrapper>
              <ConfigureStore>
                <ResizeIframeProvider>
                  <App />
                </ResizeIframeProvider>
              </ConfigureStore>
            </LegacyAppWrapper>
          </FlagProvider>
        </EcommAppProvider>
      </HeadlessAppProvider>
    </ErrorBoundary>
  );
})();
